import React, { useEffect, useState, useRef } from 'react';

/**
 * ShockwaveEffect component - renders a shockwave animation that expands from a center point
 * Used for strategem 113 (Demoralizing Warcry) to make the strategem visible
 * @param {Object} props
 * @param {number} props.x - X position in pixels (center of shockwave)
 * @param {number} props.y - Y position in pixels (center of shockwave)
 * @param {Function} props.onComplete - Callback when animation completes
 * @param {string} props.animationKey - Unique key for this animation instance
 */
export const ShockwaveEffect = ({ x, y, onComplete, animationKey }) => {
  const [isVisible, setIsVisible] = useState(true);
  const animationRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    if (!animationKey) return;

    console.log('🌊 ShockwaveEffect: Starting animation', { animationKey, x, y });
    setIsVisible(true);
    startTimeRef.current = Date.now();

    const DURATION = 800; // 800ms animation duration

    const animate = () => {
      if (!startTimeRef.current) return;

      const elapsed = Date.now() - startTimeRef.current;
      const progress = Math.min(elapsed / DURATION, 1);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // Animation complete
        console.log('✅ ShockwaveEffect: Animation complete', { animationKey });
        setIsVisible(false);
        if (onComplete) {
          onComplete();
        }
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [animationKey, x, y, onComplete]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        left: `${x}px`,
        top: `${y}px`,
        transform: 'translate(-50%, -50%)',
        width: '600px',
        height: '600px',
        pointerEvents: 'none',
        zIndex: 9999,
        opacity: isVisible ? 1 : 0,
      }}
      data-animation-key={animationKey}
    >
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          border: '4px solid rgba(255, 255, 255, 0.3)',
          boxShadow: `
            0 0 20px rgba(255, 255, 255, 0.2),
            0 0 40px rgba(255, 255, 255, 0.15),
            0 0 60px rgba(255, 255, 255, 0.1),
            inset 0 0 20px rgba(255, 255, 255, 0.1)
          `,
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0) 70%)',
          animation: 'shockwave-expand 0.8s ease-out forwards',
        }}
      />
      <style>{`
        @keyframes shockwave-expand {
          0% {
            transform: translate(-50%, -50%) scale(0);
            opacity: 0.4;
          }
          50% {
            opacity: 0.3;
          }
          100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default ShockwaveEffect;
